/**
 * Store Synchronization Utility
 *
 * This module acts as a mediator between auth.store and user-preferences.store
 * using a callback registration pattern to completely break the circular dependency.
 *
 * Both stores need to communicate with each other:
 * - auth.store needs to initialize/reset preferences on login/logout
 * - user-preferences.store needs to update auth user when preferences change
 *
 * By using registered callbacks instead of imports, we avoid any circular
 * dependency detection while maintaining the same functionality.
 */

import type { AuthToken, User } from '../types/auth';
import type { UserPreferences } from '../types/user-preferences.types';

// =============================================================================
// CALLBACK TYPES
// =============================================================================

type InitializePreferencesCallback = (
  preferences: UserPreferences | null | undefined
) => void;
type ResetPreferencesCallback = () => void;
type UpdateAuthUserCallback = (updates: Partial<User>) => void;
type GetAuthUserCallback = () => User | null;
type GetMeasurementSystemCallback = () => UserPreferences['measurement_system'];
type GetAccountUsernameCallback = () => string | null;
type ApplyRefreshedTokenCallback = (token: AuthToken) => void;
type EndAuthSessionCallback = () => void;
type ReadSessionUserCallback = () => Promise<void>;

// =============================================================================
// CALLBACK REGISTRY
// =============================================================================

// Callbacks registered by user-preferences.store
let initializePreferencesCallback: InitializePreferencesCallback | null = null;
let resetPreferencesCallback: ResetPreferencesCallback | null = null;

// Callbacks registered by auth.store
let updateAuthUserCallback: UpdateAuthUserCallback | null = null;
let getAuthUserCallback: GetAuthUserCallback | null = null;

// Accessor registered by user-preferences.store so the unit-agnostic service
// layer (CrudService) can read the current measurement system at cache-key time
// without importing the store (which would create a circular dependency).
let getMeasurementSystemCallback: GetMeasurementSystemCallback | null = null;

// Accessor registered by auth.store so a cache can put the signed-in account
// in its key — the same shape as the measurement-system accessor, for the same
// reason: the cached payload depends on it, so the key must carry it.
let getAccountUsernameCallback: GetAccountUsernameCallback | null = null;

// Callbacks registered by auth.store so the bearer lifecycle (a service, which
// the store itself imports) can tell the store about a refreshed bearer, or
// that the session is over, without importing the store back.
let applyRefreshedTokenCallback: ApplyRefreshedTokenCallback | null = null;
let endAuthSessionCallback: EndAuthSessionCallback | null = null;

// Registered by session-user, which imports auth.store, so notification.store
// can have the account read again without importing either.
let readSessionUserCallback: ReadSessionUserCallback | null = null;

// =============================================================================
// REGISTRATION FUNCTIONS
// =============================================================================

/**
 * Register callbacks from user-preferences.store.
 * Called once during store initialization.
 */
export const registerPreferencesStoreCallbacks = (callbacks: {
  initializePreferences: InitializePreferencesCallback;
  resetPreferences: ResetPreferencesCallback;
}): void => {
  initializePreferencesCallback = callbacks.initializePreferences;
  resetPreferencesCallback = callbacks.resetPreferences;
};

/**
 * Register callbacks from auth.store.
 * Called once during store initialization.
 */
export const registerAuthStoreCallbacks = (callbacks: {
  updateUser: UpdateAuthUserCallback;
  getUser: GetAuthUserCallback;
}): void => {
  updateAuthUserCallback = callbacks.updateUser;
  getAuthUserCallback = callbacks.getUser;
};

/**
 * Register the measurement-system accessor from user-preferences.store.
 * Called once during store initialization (module-load side effect).
 */
export const registerMeasurementSystemAccessor = (
  callback: GetMeasurementSystemCallback
): void => {
  getMeasurementSystemCallback = callback;
};

/**
 * Register the account accessor from auth.store.
 * Called once during store initialization (module-load side effect).
 */
export const registerAccountAccessor = (
  callback: GetAccountUsernameCallback
): void => {
  getAccountUsernameCallback = callback;
};

/**
 * Register the auth session callbacks from auth.store.
 * Called once during store initialization (module-load side effect).
 */
export const registerAuthSessionCallbacks = (callbacks: {
  applyRefreshedToken: ApplyRefreshedTokenCallback;
  endSession: EndAuthSessionCallback;
}): void => {
  applyRefreshedTokenCallback = callbacks.applyRefreshedToken;
  endAuthSessionCallback = callbacks.endSession;
};

/**
 * Register the session user read from session-user.
 * Called once when that module loads (module-load side effect).
 */
export const registerSessionUserReader = (
  callback: ReadSessionUserCallback
): void => {
  readSessionUserCallback = callback;
};

/**
 * Read the current measurement system for cache-key generation.
 * Returns 'metric' as a stable, deterministic default when no accessor is
 * registered (e.g. in unit tests or non-app contexts).
 */
export const syncGetMeasurementSystem =
  (): UserPreferences['measurement_system'] => {
    return getMeasurementSystemCallback
      ? getMeasurementSystemCallback()
      : 'metric';
  };

/**
 * Read the signed-in account's username for cache-key generation.
 * `null` when nobody is signed in, or when no accessor is registered (unit
 * tests, non-app contexts).
 */
export const syncGetAccountUsername = (): string | null => {
  return getAccountUsernameCallback ? getAccountUsernameCallback() : null;
};

// =============================================================================
// SYNCHRONIZATION FUNCTIONS (called by auth.store)
// =============================================================================

/**
 * Initialize user preferences from authenticated user data.
 * Called by auth.store after successful login/register.
 *
 * @param preferences - The user's preferences from the auth response
 */
export const syncInitializePreferences = (
  preferences: UserPreferences | null | undefined
): void => {
  if (initializePreferencesCallback && preferences) {
    initializePreferencesCallback(preferences);
  }
};

/**
 * Reset user preferences to defaults.
 * Called by auth.store after logout.
 */
export const syncResetPreferences = (): void => {
  if (resetPreferencesCallback) {
    resetPreferencesCallback();
  }
};

// =============================================================================
// SYNCHRONIZATION FUNCTIONS (called by user-preferences.store)
// =============================================================================

/**
 * Update the authenticated user's preferences in auth.store.
 * Called by user-preferences.store after successfully updating preferences.
 *
 * @param preferences - The updated preferences to sync to auth.store
 */
export const syncUpdateAuthUserPreferences = (
  preferences: UserPreferences
): void => {
  if (updateAuthUserCallback && getAuthUserCallback) {
    const user = getAuthUserCallback();
    if (user) {
      updateAuthUserCallback({ preferences });
    }
  }
};

/**
 * Get the current authenticated user's preferences from auth.store.
 * Called by user-preferences.store to sync with auth state.
 *
 * @returns The user's preferences or null if not authenticated
 */
export const syncGetAuthUserPreferences = (): UserPreferences | null => {
  if (getAuthUserCallback) {
    const user = getAuthUserCallback();
    return user?.preferences ?? null;
  }
  return null;
};

/**
 * Check if there is an authenticated user.
 * Called by user-preferences.store before attempting to sync.
 *
 * @returns True if user is authenticated
 */
export const syncHasAuthenticatedUser = (): boolean => {
  if (getAuthUserCallback) {
    return getAuthUserCallback() !== null;
  }
  return false;
};

// =============================================================================
// SYNCHRONIZATION FUNCTIONS (called by the bearer lifecycle service)
// =============================================================================

/**
 * Tell auth.store a refresh produced a new bearer, so the expiry the store
 * judges a session by is the new bearer's and not the one it replaced.
 *
 * @param token - The refreshed token, as `TokenRefreshService` returned it
 */
export const syncApplyRefreshedToken = (token: AuthToken): void => {
  if (applyRefreshedTokenCallback) {
    applyRefreshedTokenCallback(token);
  }
};

/**
 * Tell auth.store the session is over — the API refused the bearer.
 */
export const syncEndAuthSession = (): void => {
  if (endAuthSessionCallback) {
    endAuthSessionCallback();
  }
};

// =============================================================================
// SYNCHRONIZATION FUNCTIONS (called by the request coordinator)
// =============================================================================

/**
 * Mark the signed-in account unverified: the API refused a call with
 * `EMAIL_NOT_VERIFIED`, so its address isn't verified, whatever the stored
 * user says. Each platform's gate reads `email_verified` (email verification
 * & two-factor PRD, D3). Nothing happens when nobody is signed in, when the
 * user already reads unverified, or when no store is registered.
 */
export const syncMarkEmailUnverified = (): void => {
  if (!updateAuthUserCallback || !getAuthUserCallback) return;

  const user = getAuthUserCallback();
  if (user && user.email_verified !== false) {
    updateAuthUserCallback({ email_verified: false });
  }
};

// =============================================================================
// SYNCHRONIZATION FUNCTIONS (called by notification.store)
// =============================================================================

/**
 * Read the session user from the server now: an account notice arrived, so
 * the account changed elsewhere (social sign-in PRD, Task 10.3). Nothing
 * happens when no reader is registered (unit tests, non-app contexts).
 */
export const syncReadSessionUser = (): void => {
  if (readSessionUserCallback) {
    void readSessionUserCallback();
  }
};
